/**
 * Единая типизированная точка доступа к localStorage / sessionStorage.
 *
 * Прямой `localStorage` / `sessionStorage` в `src/**` запрещён вне файлов-владельцев
 * (auth-client, table-storage, storage-migration, auth-provider/cleanup — full wipe)
 * — используй эти хелперы. SSR-safe (`typeof window`) + try/catch (private mode / quota /
 * отключённый storage не должны ронять приложение). См. `.claude/rules/storage-access.md`.
 */

type Area = "local" | "session";

const resolveArea = (a: Area): Storage | null => {
    if (typeof window === "undefined") return null;
    try {
        return a === "local" ? window.localStorage : window.sessionStorage;
    } catch {
        return null; // доступ к storage может бросить (private mode / политика браузера)
    }
};

/** Прочитать строку. `null`, если ключа нет или storage недоступен. */
export function readString(key: string, opts: { area?: Area } = {}): string | null {
    const store = resolveArea(opts.area ?? "local");
    if (!store) return null;
    try {
        return store.getItem(key);
    } catch {
        return null;
    }
}

/** Записать строку. Тихо игнорирует, если storage недоступен / переполнен. */
export function writeString(key: string, value: string, opts: { area?: Area } = {}): void {
    const store = resolveArea(opts.area ?? "local");
    if (!store) return;
    try {
        store.setItem(key, value);
    } catch {
        /* quota / private mode — намеренно игнорируем */
    }
}

/** Удалить ключ. */
export function removeKey(key: string, opts: { area?: Area } = {}): void {
    const store = resolveArea(opts.area ?? "local");
    if (!store) return;
    try {
        store.removeItem(key);
    } catch {
        /* игнор */
    }
}

/** Прочитать JSON с типобезопасным fallback (битый JSON → fallback, не throw). */
export function readJSON<T>(key: string, fallback: T, opts: { area?: Area } = {}): T {
    const raw = readString(key, opts);
    if (raw === null) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

/** Записать JSON. Несериализуемое значение → no-op. */
export function writeJSON(key: string, value: unknown, opts: { area?: Area } = {}): void {
    let serialized: string;
    try {
        serialized = JSON.stringify(value);
    } catch {
        return; // circular / несериализуемое
    }
    writeString(key, serialized, opts);
}

/**
 * Реестр статических ключей storage — единое место вместо строковых литералов
 * по коду (защита от опечаток). Ключи с собственным домом (напр. язык в
 * `~/shared/config/i18n.constants`) остаются там; здесь — кросс-сквозные/ранее «сырые».
 */
export const STORAGE_KEYS = {
    activeWorkspaceId: "activeWorkspaceId",
    pendingInvitationToken: "pending_invitation_token",
    pendingProjectInviteToken: "pending_project_invite_token",
} as const;

/** Шаблонные ключи, зависящие от id. */
export const storageKeyFor = {
    chatSettings: (chatId: string) => `chat-settings:${chatId}`,
    newProjectDraft: (projectId: string) => `new-project-${projectId}`,
} as const;
